import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import {
  Bot,
  Check,
  FileText,
  Loader2,
  MessageSquare,
  Mic,
  Plus,
  Save,
  Sparkles,
  AlertTriangle,
  Lightbulb,
  ListChecks,
} from 'lucide-react';
import {
  projectAnalysisApi,
  type AiMissingItem,
  type AiReviewData,
  type AiSuggestion,
  type AnalysisItem,
} from '../lib/api/projectAnalysis';
import VoiceListenModal from './common/VoiceListenModal';

const formatNumber = (n: string | number | null | undefined): string => {
  if (n == null || n === '') return '—';
  const num = typeof n === 'string' ? parseFloat(String(n).replace(/[,٬\s]/g, '')) : n;
  if (Number.isNaN(num)) return String(n);
  return num.toLocaleString('fa-IR');
};

const priorityLabel: Record<string, string> = {
  high: 'بالا',
  medium: 'متوسط',
  low: 'پایین',
};

const priorityClass: Record<string, string> = {
  high: 'bg-red-100 text-red-800',
  medium: 'bg-amber-100 text-amber-900',
  low: 'bg-slate-100 text-slate-700',
};

interface AnalysisAiPanelProps {
  analysis: AnalysisItem;
  onUpdated: (analysis: AnalysisItem) => void;
}

type AiSubview = 'contract' | 'results' | 'chat';

const AnalysisAiPanel: React.FC<AnalysisAiPanelProps> = ({ analysis, onUpdated }) => {
  const [subview, setSubview] = useState<AiSubview>('chat');
  const [contractText, setContractText] = useState(analysis.contract_text ?? '');
  const [chatInput, setChatInput] = useState('');
  const [saving, setSaving] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [chatting, setChatting] = useState(false);
  const [applying, setApplying] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedMissing, setSelectedMissing] = useState<Set<number>>(new Set());
  const [selectedSuggestions, setSelectedSuggestions] = useState<Set<number>>(new Set());
  const [lastApplied, setLastApplied] = useState<Array<{ type?: string; label?: string }>>([]);
  const [voiceOpen, setVoiceOpen] = useState(false);
  const chatEndRef = useRef<HTMLDivElement>(null);

  const reviewData: AiReviewData = analysis.ai_review_data ?? {};
  const lastReview = reviewData.last_review;
  const missingItems = lastReview?.missing_items ?? [];
  const suggestions = lastReview?.suggestions ?? [];
  const hasReview = Boolean(lastReview?.analysis);
  const appliedIndexes = useMemo(
    () => new Set((reviewData.applied_missing_items ?? []).map((a) => a.index)),
    [reviewData.applied_missing_items]
  );
  const acceptedSuggestionKeys = useMemo(
    () => new Set((reviewData.accepted_suggestions ?? []).map((s) => `${s.target}-${s.message}`)),
    [reviewData.accepted_suggestions]
  );

  const handleChatMessage = useCallback(
    async (raw: string) => {
      const msg = raw.trim();
      if (!msg || chatting) return;
      setChatting(true);
      setError(null);
      setChatInput('');
      setSubview('chat');
      setLastApplied([]);
      try {
        const res = await projectAnalysisApi.aiChat(analysis.id, msg);
        if (!res.success) throw new Error(res.message ?? 'خطا');
        if (res.data) onUpdated(res.data);
        if (res.applied_actions?.length) setLastApplied(res.applied_actions);
        setVoiceOpen(false);
      } catch (e) {
        setError(e instanceof Error ? e.message : 'خطا در گفتگو');
      } finally {
        setChatting(false);
      }
    },
    [analysis.id, chatting, onUpdated]
  );

  useEffect(() => {
    setContractText(analysis.contract_text ?? '');
  }, [analysis.contract_text, analysis.id]);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [reviewData.conversation?.length, chatting]);

  const handleChat = async () => {
    await handleChatMessage(chatInput);
  };

  const handleVoiceConfirm = (text: string) => {
    setChatInput(text);
    void handleChatMessage(text);
  };

  const handleSaveContract = async () => {
    setSaving(true);
    setError(null);
    try {
      const res = await projectAnalysisApi.saveContractText(analysis.id, contractText);
      onUpdated(res.data);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'خطا در ذخیره');
    } finally {
      setSaving(false);
    }
  };

  const handleProcessAi = async () => {
    if (!contractText.trim()) {
      setError('ابتدا متن قرارداد را وارد کنید.');
      return;
    }
    if (contractText !== (analysis.contract_text ?? '')) {
      await handleSaveContract();
    }
    setProcessing(true);
    setError(null);
    try {
      const res = await projectAnalysisApi.processAi(analysis.id);
      if (!res.success) throw new Error(res.message ?? 'خطا در بررسی');
      if (res.data) onUpdated(res.data);
      setSelectedMissing(new Set());
      setSelectedSuggestions(new Set());
      setSubview('results');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'خطا در بررسی هوشمند');
    } finally {
      setProcessing(false);
    }
  };

  const toggleMissing = (index: number) => {
    setSelectedMissing((prev) => {
      const next = new Set(prev);
      if (next.has(index)) next.delete(index);
      else next.add(index);
      return next;
    });
  };

  const toggleSuggestion = (index: number) => {
    setSelectedSuggestions((prev) => {
      const next = new Set(prev);
      if (next.has(index)) next.delete(index);
      else next.add(index);
      return next;
    });
  };

  const handleApplyMissing = async (indexes: number[]) => {
    if (indexes.length === 0) return;
    setApplying(true);
    setError(null);
    try {
      const res = await projectAnalysisApi.applyAiMissingItems(analysis.id, indexes);
      if (res.data) onUpdated(res.data);
      setSelectedMissing(new Set());
    } catch (e) {
      setError(e instanceof Error ? e.message : 'خطا در افزودن آیتم‌ها');
    } finally {
      setApplying(false);
    }
  };

  const handleAcceptSuggestions = async (indexes: number[]) => {
    if (indexes.length === 0) return;
    setApplying(true);
    setError(null);
    try {
      const res = await projectAnalysisApi.acceptAiSuggestions(analysis.id, indexes);
      if (res.data) onUpdated(res.data);
      setSelectedSuggestions(new Set());
    } catch (e) {
      setError(e instanceof Error ? e.message : 'خطا در ثبت پیشنهادات');
    } finally {
      setApplying(false);
    }
  };

  const isSuggestionAccepted = (s: AiSuggestion) =>
    acceptedSuggestionKeys.has(`${s.target}-${s.message}`);

  const subviewBtn = (key: AiSubview, label: string, icon: React.ReactNode, badge?: number) => (
    <button
      type="button"
      onClick={() => setSubview(key)}
      className={`shrink-0 inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs sm:text-sm font-bold transition-colors ${
        subview === key
          ? 'bg-[#0a4d29] text-white'
          : 'bg-white text-slate-600 hover:bg-slate-100 border border-slate-200'
      }`}
    >
      {icon}
      {label}
      {badge != null && badge > 0 && (
        <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${subview === key ? 'bg-white/20' : 'bg-emerald-100 text-emerald-800'}`}>
          {badge}
        </span>
      )}
    </button>
  );

  const contractPanel = (
    <div className="flex flex-col h-full min-h-0">
      <div className="shrink-0 px-4 py-3 border-b border-slate-200 bg-slate-50">
        <div className="flex items-center gap-2">
          <FileText size={18} className="text-[#0a4d29]" />
          <h3 className="font-bold text-slate-800 text-sm">متن قرارداد</h3>
        </div>
        <p className="text-xs text-slate-500 mt-1">متن قرارداد را paste کنید، سپس بررسی هوشمند را اجرا کنید.</p>
      </div>
      <div className="flex-1 min-h-0 p-4 flex flex-col gap-3">
        <textarea
          value={contractText}
          onChange={(e) => setContractText(e.target.value)}
          placeholder="بندهای مهم قرارداد، شرح کار، مبالغ، واحدها..."
          className="flex-1 min-h-[200px] w-full px-4 py-3 border border-slate-200 rounded-xl text-sm leading-relaxed focus:ring-2 focus:ring-[#0a4d29]/20 focus:border-[#0a4d29] outline-none resize-none bg-white"
          dir="rtl"
        />
        <div className="flex flex-wrap gap-2 justify-end shrink-0">
          <button
            type="button"
            onClick={handleSaveContract}
            disabled={saving}
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl border border-slate-300 bg-white text-slate-700 font-bold text-sm hover:bg-slate-50 disabled:opacity-50"
          >
            {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
            ذخیره
          </button>
          <button
            type="button"
            onClick={handleProcessAi}
            disabled={processing || !contractText.trim()}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-[#0a4d29] text-white font-bold text-sm hover:bg-emerald-900 disabled:opacity-50"
          >
            {processing ? <Loader2 size={18} className="animate-spin" /> : <Sparkles size={18} />}
            بررسی با AI
          </button>
        </div>
      </div>
    </div>
  );

  const resultsPanel = (
    <div className="flex flex-col h-full min-h-0 overflow-y-auto">
      {!hasReview && missingItems.length === 0 && suggestions.length === 0 ? (
        <div className="flex flex-col items-center justify-center flex-1 p-8 text-center">
          <div className="w-14 h-14 rounded-2xl bg-emerald-50 flex items-center justify-center mb-4">
            <Bot size={28} className="text-[#0a4d29]" />
          </div>
          <p className="font-bold text-slate-700 mb-1">هنوز بررسی انجام نشده</p>
          <p className="text-sm text-slate-500 max-w-sm">متن قرارداد را وارد کنید و دکمه «بررسی با AI» را بزنید.</p>
          <button
            type="button"
            onClick={() => setSubview('contract')}
            className="mt-4 text-sm font-bold text-emerald-700 hover:underline"
          >
            رفتن به متن قرارداد
          </button>
        </div>
      ) : (
        <div className="p-4 space-y-4">
          {lastReview?.analysis && (
            <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
              <div className="px-4 py-3 border-b border-slate-100 bg-slate-50">
                <span className="font-bold text-slate-800 text-sm">خلاصه تحلیل</span>
              </div>
              <div className="p-4 space-y-3 text-sm">
                {lastReview.analysis.summary && (
                  <p className="text-slate-700 leading-relaxed">{lastReview.analysis.summary}</p>
                )}
                {lastReview.analysis.strengths && lastReview.analysis.strengths.length > 0 && (
                  <div className="rounded-lg bg-emerald-50 p-3">
                    <p className="font-bold text-emerald-800 mb-2 text-xs">نقاط قوت</p>
                    <ul className="space-y-1 text-emerald-900/90 text-sm">
                      {lastReview.analysis.strengths.map((s, i) => (
                        <li key={i} className="flex gap-2"><Check size={14} className="shrink-0 mt-0.5 text-emerald-600" />{s}</li>
                      ))}
                    </ul>
                  </div>
                )}
                {lastReview.analysis.issues && lastReview.analysis.issues.length > 0 && (
                  <div className="rounded-lg bg-red-50 p-3">
                    <p className="font-bold text-red-700 mb-2 text-xs">مشکلات / کمبودها</p>
                    <ul className="space-y-1 text-red-900/90 text-sm">
                      {lastReview.analysis.issues.map((s, i) => (
                        <li key={i} className="flex gap-2"><AlertTriangle size={14} className="shrink-0 mt-0.5 text-red-500" />{s}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </div>
          )}

          {missingItems.length > 0 && (
            <div className="rounded-xl border border-amber-200 bg-white overflow-hidden">
              <div className="px-4 py-3 border-b border-amber-100 bg-amber-50 flex flex-wrap items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                  <Plus size={16} className="text-amber-700" />
                  <span className="font-bold text-amber-900 text-sm">آیتم‌های پیشنهادی</span>
                  <span className="text-xs bg-amber-200 text-amber-900 px-2 py-0.5 rounded-full">{missingItems.length}</span>
                </div>
                <button
                  type="button"
                  disabled={applying || selectedMissing.size === 0}
                  onClick={() => handleApplyMissing([...selectedMissing])}
                  className="text-xs font-bold px-3 py-1.5 rounded-lg bg-[#0a4d29] text-white disabled:opacity-40"
                >
                  افزودن ({selectedMissing.size})
                </button>
              </div>
              <div className="divide-y divide-amber-50">
                {missingItems.map((item: AiMissingItem, index: number) => {
                  const applied = appliedIndexes.has(index);
                  return (
                    <label
                      key={index}
                      className={`flex gap-3 p-3 cursor-pointer hover:bg-amber-50/50 ${applied ? 'opacity-50 bg-emerald-50/30' : ''}`}
                    >
                      <div className="pt-0.5 shrink-0">
                        {!applied ? (
                          <input
                            type="checkbox"
                            checked={selectedMissing.has(index)}
                            onChange={() => toggleMissing(index)}
                            className="w-4 h-4 rounded border-amber-400 text-emerald-700"
                          />
                        ) : (
                          <Check size={18} className="text-emerald-600" />
                        )}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2 mb-1">
                          <span className="font-bold text-slate-800 text-sm">{item.name}</span>
                          {item.section && (
                            <span className="text-[10px] bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full">{item.section}</span>
                          )}
                          {applied && <span className="text-[10px] text-emerald-700 font-bold">افزوده شد</span>}
                        </div>
                        <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-600">
                          {item.unit && <span>واحد: {item.unit}</span>}
                          {item.estimated_quantity != null && <span>مقدار: {formatNumber(item.estimated_quantity)}</span>}
                          {item.estimated_unit_cost != null && <span>قیمت واحد: {formatNumber(item.estimated_unit_cost)}</span>}
                        </div>
                        {(item.reason ?? item.description) && (
                          <p className="text-xs text-slate-500 mt-1">{item.reason ?? item.description}</p>
                        )}
                      </div>
                    </label>
                  );
                })}
              </div>
            </div>
          )}

          {suggestions.length > 0 && (
            <div className="rounded-xl border border-blue-200 bg-white overflow-hidden">
              <div className="px-4 py-3 border-b border-blue-100 bg-blue-50 flex flex-wrap items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                  <Lightbulb size={16} className="text-blue-700" />
                  <span className="font-bold text-blue-900 text-sm">پیشنهادات</span>
                  <span className="text-xs bg-blue-200 text-blue-900 px-2 py-0.5 rounded-full">{suggestions.length}</span>
                </div>
                <button
                  type="button"
                  disabled={applying || selectedSuggestions.size === 0}
                  onClick={() => handleAcceptSuggestions([...selectedSuggestions])}
                  className="text-xs font-bold px-3 py-1.5 rounded-lg bg-[#0a4d29] text-white disabled:opacity-40"
                >
                  ثبت ({selectedSuggestions.size})
                </button>
              </div>
              <ul className="divide-y divide-blue-50">
                {suggestions.map((s: AiSuggestion, index: number) => {
                  const accepted = isSuggestionAccepted(s);
                  const pri = s.priority ?? 'medium';
                  return (
                    <li key={index} className={`p-3 flex gap-3 ${accepted ? 'bg-emerald-50/40' : ''}`}>
                      {!accepted ? (
                        <input
                          type="checkbox"
                          checked={selectedSuggestions.has(index)}
                          onChange={() => toggleSuggestion(index)}
                          className="w-4 h-4 mt-1 rounded border-blue-300 text-emerald-700 shrink-0"
                        />
                      ) : (
                        <Check size={16} className="text-emerald-600 shrink-0 mt-0.5" />
                      )}
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2 mb-1">
                          <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${priorityClass[pri] ?? priorityClass.medium}`}>
                            {priorityLabel[pri] ?? pri}
                          </span>
                          {s.target && <span className="text-xs text-blue-800">{s.target}</span>}
                          {accepted && <span className="text-xs text-emerald-700 font-bold">ثبت شد</span>}
                        </div>
                        <p className="text-sm text-slate-800 leading-relaxed">{s.message}</p>
                      </div>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );

  const chatPanel = (
    <div className="flex flex-col h-full min-h-0">
      <div className="shrink-0 px-3 py-2.5 border-b border-emerald-100 bg-emerald-50/70">
        <p className="text-[11px] text-emerald-900/80 leading-relaxed">
          با ویس یا متن دستور بدهید — مثلاً: «کارگر ساده ۱۰ نفرروز با بهای ۲٫۵ میلیون اضافه کن»، «مالیات ۹ درصد»، «بیمه بخش مصالح ۷ درصد».
        </p>
      </div>
      <div className="flex-1 min-h-0 overflow-y-auto p-4 space-y-3 bg-slate-50/80">
        {(reviewData.conversation ?? []).length === 0 && (
          <div className="text-center py-8 px-3">
            <Bot size={32} className="mx-auto text-emerald-700/40 mb-3" />
            <p className="text-sm text-slate-600 font-medium mb-2">دستیار ثبت آنالیز</p>
            <p className="text-xs text-slate-500 max-w-sm mx-auto leading-relaxed">
              میکروفون را بزنید، حرف بزنید، دوباره بزنید تا متن شود و ثبت اعمال شود.
            </p>
            <div className="mt-4 flex flex-wrap justify-center gap-1.5">
              {['سود ۱۵ درصد بگذار', 'مالیات را ۹ کن', 'بخش نیروی انسانی اضافه کن'].map((ex) => (
                <button
                  key={ex}
                  type="button"
                  onClick={() => void handleChatMessage(ex)}
                  className="text-[11px] px-2.5 py-1.5 rounded-full bg-white border border-slate-200 text-slate-600 hover:border-emerald-300 hover:text-emerald-800"
                >
                  {ex}
                </button>
              ))}
            </div>
          </div>
        )}
        {(reviewData.conversation ?? []).map((msg, i) => (
          <div key={i} className={`flex ${msg.role === 'user' ? 'justify-start' : 'justify-end'}`}>
            <div
              className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed whitespace-pre-wrap ${
                msg.role === 'user'
                  ? 'bg-white border border-slate-200 text-slate-800'
                  : 'bg-[#0a4d29] text-white'
              }`}
            >
              {msg.content}
              {msg.role === 'assistant' && msg.applied_actions && msg.applied_actions.length > 0 && (
                <ul className="mt-2 pt-2 border-t border-white/20 space-y-0.5 text-xs text-emerald-100">
                  {msg.applied_actions.map((a, j) => (
                    <li key={j} className="flex items-start gap-1">
                      <Check size={12} className="shrink-0 mt-0.5" />
                      <span>{a.label ?? a.type}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        ))}
        {chatting && (
          <div className="flex justify-end">
            <div className="bg-[#0a4d29]/80 text-white rounded-2xl px-4 py-2.5 text-sm flex items-center gap-2">
              <Loader2 size={16} className="animate-spin" />
              در حال پردازش و ثبت...
            </div>
          </div>
        )}
        {lastApplied.length > 0 && !chatting && (
          <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-900">
            <p className="font-bold mb-1">آخرین تغییرات اعمال‌شده</p>
            <ul className="space-y-0.5">
              {lastApplied.map((a, i) => (
                <li key={i}>• {a.label ?? a.type}</li>
              ))}
            </ul>
          </div>
        )}
        <div ref={chatEndRef} />
      </div>
      {error && (
        <div className="shrink-0 px-3 py-1.5 text-[11px] border-t border-slate-100 bg-white">
          <span className="text-rose-600">{error}</span>
        </div>
      )}
      <div className="shrink-0 p-3 border-t border-slate-200 bg-white flex gap-2 items-end">
        <button
          type="button"
          onClick={() => setVoiceOpen(true)}
          disabled={chatting}
          title="دستور صوتی زنده"
          className="shrink-0 self-end min-h-[44px] min-w-[44px] rounded-xl inline-flex items-center justify-center transition-colors disabled:opacity-40 bg-emerald-50 text-emerald-800 border border-emerald-200 hover:bg-emerald-100"
        >
          <Mic size={18} />
        </button>
        <textarea
          value={chatInput}
          onChange={(e) => setChatInput(e.target.value)}
          rows={2}
          placeholder="بنویسید یا میکروفون را برای ویس زنده بزنید..."
          className="flex-1 px-3 py-2 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-[#0a4d29]/20 outline-none resize-none min-w-0"
          dir="rtl"
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              void handleChat();
            }
          }}
        />
        <button
          type="button"
          onClick={() => void handleChat()}
          disabled={chatting || !chatInput.trim()}
          className="shrink-0 self-end px-4 py-2.5 rounded-xl bg-[#0a4d29] text-white font-bold text-sm disabled:opacity-50 min-h-[44px]"
        >
          ارسال
        </button>
      </div>
      <VoiceListenModal
        open={voiceOpen}
        onClose={() => setVoiceOpen(false)}
        onConfirm={handleVoiceConfirm}
        busy={chatting}
      />
    </div>
  );

  return (
    <div className="flex-1 flex flex-col min-h-0 bg-slate-100/60">
      {/* نوار وضعیت */}
      <div className="shrink-0 px-3 sm:px-4 py-3 bg-white border-b border-slate-200 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2 min-w-0">
          <div className="w-8 h-8 rounded-lg bg-emerald-100 flex items-center justify-center shrink-0">
            <Sparkles size={16} className="text-[#0a4d29]" />
          </div>
          <div className="min-w-0">
            <p className="text-sm font-bold text-slate-800 truncate">دستیار هوشمند آنالیز</p>
            <p className="text-[11px] text-slate-500 truncate">
              ویس → متن → ثبت خودکار · مالیات، بیمه، آیتم، سود
            </p>
          </div>
        </div>
        <div className="flex gap-1.5 overflow-x-auto">
          {subviewBtn('chat', 'گفتگو / ویس', <MessageSquare size={14} />, reviewData.conversation?.length)}
          {subviewBtn('contract', 'قرارداد', <FileText size={14} />)}
          {subviewBtn('results', 'نتایج', <ListChecks size={14} />, missingItems.length + suggestions.length)}
        </div>
      </div>

      {error && (
        <div className="shrink-0 mx-3 sm:mx-4 mt-3 rounded-lg bg-red-50 border border-red-200 text-red-800 text-sm px-4 py-2.5 flex items-start gap-2">
          <AlertTriangle size={16} className="shrink-0 mt-0.5" />
          <span>{error}</span>
        </div>
      )}

      {/* موبایل: یک پنل */}
      <div className="lg:hidden flex-1 min-h-0 flex flex-col">
        <div className="flex-1 min-h-[420px] max-h-[calc(100dvh-14rem)] overflow-hidden bg-white mx-3 sm:mx-4 my-3 rounded-xl border border-slate-200 shadow-sm">
          {subview === 'contract' && contractPanel}
          {subview === 'results' && resultsPanel}
          {subview === 'chat' && chatPanel}
        </div>
      </div>

      {/* دسکتاپ: دو ستون */}
      <div className="hidden lg:grid lg:grid-cols-12 gap-4 flex-1 min-h-0 p-4 overflow-hidden">
        <div className="col-span-4 min-h-0 bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden flex flex-col">
          {contractPanel}
        </div>
        <div className="col-span-5 min-h-0 bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden flex flex-col">
          {resultsPanel}
        </div>
        <div className="col-span-3 min-h-0 bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden flex flex-col">
          <div className="shrink-0 px-4 py-3 border-b border-slate-200 bg-slate-50 flex items-center gap-2">
            <MessageSquare size={16} className="text-[#0a4d29]" />
            <span className="font-bold text-slate-800 text-sm">گفتگو</span>
          </div>
          {chatPanel}
        </div>
      </div>
    </div>
  );
};

export default AnalysisAiPanel;
